// Cache service for storing frequently accessed data
class CacheService {
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  
  // Default TTL (Time To Live) is 5 minutes
  private defaultTTL = 5 * 60 * 1000;

  /**
   * Get data from cache if it exists and is not expired
   */
  get(key: string): any | null {
    const cachedItem = this.cache.get(key);
    
    if (!cachedItem) {
      return null;
    }
    
    // Check if cache has expired
    if (Date.now() - cachedItem.timestamp > cachedItem.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return cachedItem.data;
  }

  /**
   * Set data in cache with optional TTL
   */
  set(key: string, data: any, ttl: number = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Delete specific cache entry
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Check if cache entry exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Update TTL for existing cache entry
   */
  updateTTL(key: string, ttl: number = this.defaultTTL): void {
    const cachedItem = this.cache.get(key);
    if (cachedItem) {
      cachedItem.ttl = ttl;
      cachedItem.timestamp = Date.now();
      this.cache.set(key, cachedItem);
    }
  }
}

export const cacheService = new CacheService();
export default cacheService;